"use client";

import type React from "react";
import { useEffect, useState, useMemo } from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  HStack,
  Text,
  Checkbox,
  RadioGroup,
  Radio,
  Avatar,
  Select,
  Spinner,
  FormErrorMessage,
  Badge,
  Link,
} from "@chakra-ui/react";
import { ExternalLink } from "lucide-react";
import {
  agentService,
  type Agent,
  type VoiceDetails,
} from "../../../services/agentService.ts";
import "flag-icons/css/flag-icons.css";

const LANGUAGE_LABELS: Record<string, string> = {
  "en-US": "English (US)",
  "en-IN": "English (India)",
  "en-GB": "English (UK)",
  "en-AU": "English (Australia)",
  "en-NZ": "English (New Zealand)",
  multi: "Multilingual",
};

const LANG_FLAG: Record<string, string> = {
  "en-US": "us",
  "en-IN": "in",
  "en-GB": "gb",
  "en-AU": "au",
  "en-NZ": "nz",
  "de-DE": "de",
  "es-ES": "es",
  "es-419": "mx",
  "fr-FR": "fr",
  "fr-CA": "ca",
  "ja-JP": "jp",
  "pt-BR": "br",
  "pt-PT": "pt",
  "zh-CN": "cn",
  "ru-RU": "ru",
  "it-IT": "it",
  "ko-KR": "kr",
  "nl-NL": "nl",
  "pl-PL": "pl",
  "tr-TR": "tr",
  "vi-VN": "vn",
  multi: "un",
};

export type AgentSwapFormValues = {
  name: string;
  description: string;
  agent_id: string;
  keep_current_voice: boolean;
  keep_current_language: boolean;
  post_call_analysis_setting: "both_agents" | "only_destination_agent";
  webhook_setting:
    | "both_agents"
    | "only_destination_agent"
    | "only_source_agent";
  speak_during_execution: boolean;
  execution_message_type: "prompt" | "static_text";
  execution_message_description: string;
};

export const defaultAgentSwapValues = (): AgentSwapFormValues => ({
  name: "agent_transfer",
  description: "",
  agent_id: "",
  keep_current_voice: false,
  keep_current_language: false,
  post_call_analysis_setting: "only_destination_agent",
  webhook_setting: "only_source_agent",
  speak_during_execution: false,
  execution_message_type: "prompt",
  execution_message_description: "",
});

function languageLabel(code: string | undefined): string {
  if (!code) return "—";
  return LANGUAGE_LABELS[code] || code;
}

function languageFlagClass(code: string | undefined): string {
  if (!code) return "fi fi-un";
  const c = LANG_FLAG[code] || "un";
  return `fi fi-${c}`;
}

interface AgentSwapToolFieldsProps {
  value: AgentSwapFormValues;
  onChange: (patch: Partial<AgentSwapFormValues>) => void;
  excludeAgentId?: string;
  nameError?: string;
  onNameBlur?: (name: string) => void;
  inputProps: Record<string, unknown>;
  labelColor: string;
  helperMuted: string;
  subtleBorder: string;
  subtleBg: string;
  showIntro?: boolean;
  hideIdentityFields?: boolean;
}

export const AgentSwapToolFields: React.FC<AgentSwapToolFieldsProps> = ({
  value,
  onChange,
  excludeAgentId,
  nameError,
  onNameBlur,
  inputProps,
  labelColor,
  helperMuted,
  subtleBorder,
  subtleBg,
  showIntro = true,
  hideIdentityFields = false,
}) => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [destinationVoice, setDestinationVoice] = useState<VoiceDetails | null>(
    null
  );
  const [destinationLanguage, setDestinationLanguage] = useState<string>("");
  const [sourceVoice, setSourceVoice] = useState<VoiceDetails | null>(null);
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [sourceLoading, setSourceLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);

  const selectableAgents = useMemo(
    () =>
      agents.filter((a) => !excludeAgentId || a.agent_id !== excludeAgentId),
    [agents, excludeAgentId]
  );

  useEffect(() => {
    let cancelled = false;
    setLoadingAgents(true);
    agentService
      .list()
      .then((list) => {
        if (!cancelled) setAgents(list);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingAgents(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!excludeAgentId) {
      setSourceVoice(null);
      setSourceLanguage("");
      setSourceLoading(false);
      return;
    }
    let cancelled = false;
    setSourceLoading(true);
    agentService
      .get(excludeAgentId)
      .then(async (ag) => {
        if (cancelled) return;
        setSourceLanguage(ag.language || "");
        try {
          const v = await agentService.getVoiceDetails(ag.voice_id);
          if (!cancelled) setSourceVoice(v);
        } catch {
          if (!cancelled) setSourceVoice(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSourceVoice(null);
          setSourceLanguage("");
        }
      })
      .finally(() => {
        if (!cancelled) setSourceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [excludeAgentId]);

  useEffect(() => {
    if (!value.agent_id) {
      setDestinationVoice(null);
      setDestinationLanguage("");
      setDestinationLoading(false);
      return;
    }
    let cancelled = false;
    setDestinationLoading(true);
    agentService
      .get(value.agent_id)
      .then(async (ag) => {
        if (cancelled) return;
        setDestinationLanguage(ag.language || "");
        try {
          const v = await agentService.getVoiceDetails(ag.voice_id);
          if (!cancelled) setDestinationVoice(v);
        } catch {
          if (!cancelled) setDestinationVoice(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDestinationVoice(null);
          setDestinationLanguage("");
        }
      })
      .finally(() => {
        if (!cancelled) setDestinationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [value.agent_id]);

  const voiceForDisplay = value.keep_current_voice
    ? sourceVoice
    : destinationVoice;
  const languageCodeForDisplay = value.keep_current_language
    ? sourceLanguage
    : destinationLanguage;

  const showVoiceRow =
    (value.keep_current_voice && !!excludeAgentId) ||
    (!value.keep_current_voice && !!value.agent_id);
  const showLangRow =
    (value.keep_current_language && !!excludeAgentId) ||
    (!value.keep_current_language && !!value.agent_id);
  const showPreviewCard = showVoiceRow || showLangRow;

  const voiceLinkAgentId = value.keep_current_voice
    ? excludeAgentId
    : value.agent_id;

  const voiceRowLoading = value.keep_current_voice
    ? sourceLoading
    : !!value.agent_id && destinationLoading;
  const langRowLoading = value.keep_current_language
    ? sourceLoading
    : !!value.agent_id && destinationLoading;

  const voiceHelper = value.keep_current_voice
    ? "It will keep the current voice for the entire call."
    : "It will use the voice that you set for the selected agent.";

  const languageHelper = value.keep_current_language
    ? "Use the currently selected language after the transfer."
    : "Use the language that you set for the selected agent.";

  return (
    <VStack spacing={4} align="stretch">
      {showIntro && (
        <Text fontSize="sm" color={helperMuted}>
          Transfer to another agent to bring in new capabilities and fulfill
          different tasks. It will be a seamless transition, with all the call
          context preserved. It will appear as a single call in history.
        </Text>
      )}

      {!hideIdentityFields && (
        <>
          <FormControl isRequired isInvalid={!!nameError}>
            <FormLabel color={labelColor}>Function Name</FormLabel>
            <Input
              value={value.name}
              onChange={(e) => onChange({ name: e.target.value })}
              onBlur={(e) => onNameBlur?.(e.target.value)}
              placeholder="agent_transfer"
              variant="outline"
              {...inputProps}
            />
            {nameError && <FormErrorMessage>{nameError}</FormErrorMessage>}
          </FormControl>

          <FormControl>
            <FormLabel color={labelColor}>Description</FormLabel>
            <Textarea
              value={value.description}
              onChange={(e) => onChange({ description: e.target.value })}
              placeholder="e.g. Transfer the call to the customer service agent"
              rows={3}
              variant="outline"
              {...inputProps}
            />
          </FormControl>
        </>
      )}

      <Box>
        <HStack
          justify="space-between"
          align="flex-end"
          spacing={3}
          mb={2}
          flexWrap="wrap"
        >
          <FormLabel mb={0} color={labelColor} fontWeight="semibold">
            Select Agent
          </FormLabel>
          <HStack spacing={4} flexShrink={0}>
            <Checkbox
              size="sm"
              isChecked={value.keep_current_voice}
              onChange={(e) =>
                onChange({ keep_current_voice: e.target.checked })
              }
            >
              <Text fontSize="xs" color={labelColor}>
                Keep the same voice
              </Text>
            </Checkbox>
            <Checkbox
              size="sm"
              isChecked={value.keep_current_language}
              onChange={(e) =>
                onChange({ keep_current_language: e.target.checked })
              }
            >
              <Text fontSize="xs" color={labelColor}>
                Keep the current language
              </Text>
            </Checkbox>
          </HStack>
        </HStack>

        <FormControl isRequired>
          {loadingAgents ? (
            <Spinner size="sm" />
          ) : (
            <Select
              placeholder="Choose an agent"
              value={value.agent_id}
              onChange={(e) => onChange({ agent_id: e.target.value })}
              bg={(inputProps as { bg?: string }).bg}
              borderColor={
                (inputProps as { borderColor?: string }).borderColor
              }
              _focus={(inputProps as { _focus?: object })._focus}
            >
              {selectableAgents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.agent_name || a.agent_id}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
      </Box>

      {showPreviewCard && (
        <Box
          p={3}
          borderRadius="md"
          borderWidth="1px"
          borderColor={subtleBorder}
          bg={subtleBg}
        >
          <VStack align="stretch" spacing={3}>
            {showVoiceRow && (
              <Box>
                <HStack justify="space-between" align="center" mb={1}>
                  <HStack spacing={2} flexWrap="wrap" align="center">
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color={labelColor}
                    >
                      Voice
                    </Text>
                    <Badge
                      px={2}
                      py={1}
                      borderRadius="full"
                      variant="subtle"
                      colorScheme="gray"
                      fontWeight="normal"
                      display="inline-flex"
                      alignItems="center"
                      gap={1}
                    >
                      {voiceRowLoading ? (
                        <Spinner size="xs" />
                      ) : voiceForDisplay ? (
                        <>
                          <Avatar
                            size="xs"
                            name={voiceForDisplay.voice_name || "?"}
                            src={voiceForDisplay.avatar_url}
                          />
                          <Text as="span" fontSize="sm">
                            {voiceForDisplay.voice_name || "—"}
                          </Text>
                        </>
                      ) : (
                        <Text as="span" fontSize="sm">
                          —
                        </Text>
                      )}
                    </Badge>
                  </HStack>
                  {voiceLinkAgentId ? (
                    <Link
                      href={`/agents/${voiceLinkAgentId}`}
                      isExternal
                      aria-label={
                        value.keep_current_voice
                          ? "Open this agent in a new tab"
                          : "Open selected agent in a new tab"
                      }
                      color={helperMuted}
                      _hover={{ opacity: 0.85 }}
                    >
                      <ExternalLink size={16} />
                    </Link>
                  ) : null}
                </HStack>
                <Text fontSize="xs" color={helperMuted} mt={1}>
                  {voiceHelper}
                </Text>
              </Box>
            )}

            {showLangRow && (
              <Box
                borderTopWidth={showVoiceRow ? "1px" : undefined}
                borderTopStyle={showVoiceRow ? "dashed" : undefined}
                borderColor={subtleBorder}
                pt={showVoiceRow ? 3 : 0}
              >
                <HStack spacing={2} flexWrap="wrap" align="center" mb={1}>
                  <Text fontSize="sm" fontWeight="semibold" color={labelColor}>
                    Language
                  </Text>
                  <Badge
                    px={2}
                    py={1}
                    borderRadius="full"
                    variant="subtle"
                    colorScheme="gray"
                    fontWeight="normal"
                    display="inline-flex"
                    alignItems="center"
                    gap={1}
                  >
                    {langRowLoading ? (
                      <Spinner size="xs" />
                    ) : (
                      <>
                        <Box
                          as="span"
                          className={languageFlagClass(languageCodeForDisplay)}
                          aria-hidden
                          sx={{
                            width: "1em",
                            height: "1em",
                            borderRadius: "full",
                            backgroundSize: "cover",
                          }}
                        />
                        <Text as="span" fontSize="sm">
                          {languageLabel(languageCodeForDisplay)}
                        </Text>
                      </>
                    )}
                  </Badge>
                </HStack>
                <Text fontSize="xs" color={helperMuted} mt={1}>
                  {languageHelper}
                </Text>
              </Box>
            )}
          </VStack>
        </Box>
      )}

      <FormControl>
        <FormLabel color={labelColor}>Post Call Analysis Setting</FormLabel>
        <Text fontSize="xs" color={helperMuted} mb={2}>
          Select which agent&apos;s analysis fields to include in the call
          history.
        </Text>
        <RadioGroup
          value={value.post_call_analysis_setting}
          onChange={(v) =>
            onChange({
              post_call_analysis_setting:
                v as AgentSwapFormValues["post_call_analysis_setting"],
            })
          }
        >
          <VStack align="stretch" spacing={2}>
            <Radio value="only_destination_agent">
              Only transferred agent
            </Radio>
            <Radio value="both_agents">
              Both this agent and transferred agent
            </Radio>
          </VStack>
        </RadioGroup>
      </FormControl>

      <FormControl>
        <FormLabel color={labelColor}>Webhook Setting</FormLabel>
        <Text fontSize="xs" color={helperMuted} mb={2}>
          Select which agent&apos;s webhook endpoint to use for sending call
          updates.
        </Text>
        <RadioGroup
          value={value.webhook_setting}
          onChange={(v) =>
            onChange({
              webhook_setting: v as AgentSwapFormValues["webhook_setting"],
            })
          }
        >
          <VStack align="stretch" spacing={2}>
            <Radio value="only_destination_agent">
              Only transferred agent
            </Radio>
            <Radio value="both_agents">
              Both this agent and transferred agent
            </Radio>
            <Radio value="only_source_agent">Only this agent</Radio>
          </VStack>
        </RadioGroup>
      </FormControl>

      <Checkbox
        isChecked={value.speak_during_execution}
        onChange={(e) =>
          onChange({ speak_during_execution: e.target.checked })
        }
      >
        <Box>
          <Text fontSize="sm" fontWeight="medium" color={labelColor}>
            Talk While Waiting
          </Text>
          <Text fontSize="xs" color={helperMuted}>
            Enable to say a short phrase to fill the silence, like &quot;let me
            look that up for you&quot;.
          </Text>
        </Box>
      </Checkbox>

      {value.speak_during_execution && (
        <FormControl>
          <FormLabel color={labelColor}>Prompt</FormLabel>
          <RadioGroup
            value={value.execution_message_type}
            onChange={(v) =>
              onChange({
                execution_message_type: v as "prompt" | "static_text",
              })
            }
            mb={2}
          >
            <HStack spacing={6}>
              <Radio value="prompt">Prompt</Radio>
              <Radio value="static_text">Static Sentence</Radio>
            </HStack>
          </RadioGroup>
          <Textarea
            value={value.execution_message_description}
            onChange={(e) =>
              onChange({ execution_message_description: e.target.value })
            }
            placeholder="Enter the execution message description"
            rows={3}
            variant="outline"
            {...inputProps}
          />
        </FormControl>
      )}
    </VStack>
  );
};
